import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { ChainId, MapData } from '@pleco/shared';
import { formatPercent, gradeColor, isChainId } from '@pleco/shared';

import { BubbleCanvas } from '../components/BubbleCanvas.js';
import { api } from '../lib/api.js';

/**
 * Embeddable view: no chrome, no navigation — a single map that any dApp or
 * token page can drop in with an <iframe>.
 *
 *   <iframe src="https://.../embed/sol/<mint>?limit=120" width="100%" height="520" />
 */
export function EmbedPage() {
  const { chain: chainParam, address } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState<MapData>();
  const [error, setError] = useState<string>();

  const chain: ChainId = chainParam && isChainId(chainParam) ? chainParam : 'sol';

  useEffect(() => {
    if (!address) return;
    api
      .map(chain, address, {
        limit: Number(params.get('limit') ?? 100),
        magic: params.get('magic') !== '0',
        time: params.get('time') !== '0',
        timestamp: params.get('t') ? Number(params.get('t')) : undefined,
      })
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [address, chain, params]);

  if (error) {
    return <div className="canvas-empty">{error}</div>;
  }
  if (!data) {
    return (
      <div className="canvas-empty">
        <span className="spinner" style={{ width: 20, height: 20 }} />
      </div>
    );
  }

  const d = data.metrics.decentralization;

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-1)',
        }}
      >
        <strong>{data.token.symbol}</strong>
        <span className="chip" style={{ color: gradeColor(d.grade), borderColor: gradeColor(d.grade) }}>
          score {d.score.toFixed(0)} · {d.grade}
        </span>
        {data.clusters[0] && (
          <span className="chip" style={{ color: data.clusters[0].color }}>
            top cluster {formatPercent(data.clusters[0].percent)}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <a
          className="faint"
          style={{ fontSize: 11 }}
          href={`/map/${chain}/${address}`}
          target="_blank"
          rel="noreferrer"
        >
          TrapMap ↗
        </a>
      </div>
      <BubbleCanvas
        data={data}
        focusMode
        showLabels={params.get('labels') !== '0'}
        onSelect={() => undefined}
      />
    </div>
  );
}
